import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, XCircle, RotateCcw, Trophy } from "lucide-react";
import api from "../lib/api";

export default function Quiz() {
  const { quizId } = useParams();
  const navigate = useNavigate();
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [current, setCurrent] = useState(0);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/quizzes/${quizId}`);
        setQuiz(data);
        setAnswers(new Array(data.questions.length).fill(-1));
      } catch (e) {
        if (e.response?.status === 402) {
          navigate("/pricing", { replace: true });
        }
      }
    })();
  }, [quizId, navigate]);

  const total = quiz?.questions?.length || 0;
  const answeredAll = useMemo(() => answers.length > 0 && answers.every((a) => a >= 0), [answers]);

  if (!quiz) return <div className="p-10 text-slate-500">Loading…</div>;

  if (result) return <QuizResult quiz={quiz} result={result} onRetake={() => { setResult(null); setAnswers(new Array(total).fill(-1)); setCurrent(0); }} />;

  const q = quiz.questions[current];
  const pick = (i) => {
    const next = [...answers];
    next[current] = i;
    setAnswers(next);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/quizzes/submit", { quiz_id: quiz.quiz_id, answers });
      setResult(data);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="quiz-page">
      <div className="max-w-3xl mx-auto p-6 lg:p-10">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-orange-600">
          <ArrowLeft className="w-4 h-4" /> Exit quiz
        </button>

        <div className="mt-6">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Certification quiz · Pass {quiz.pass_score}%</div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-950 mt-2">{quiz.title}</h1>
        </div>

        {/* Progress */}
        <div className="mt-6 flex items-center justify-between">
          <div className="text-sm font-mono text-slate-500">
            {String(current + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
          </div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">
            {answers.filter((a) => a >= 0).length} answered
          </div>
        </div>
        <div className="mt-2 w-full h-1 bg-slate-100">
          <div className="h-full bg-orange-600 transition-all" style={{ width: `${((current + 1) / total) * 100}%` }} />
        </div>

        {/* Question */}
        <div className="mt-10">
          <div className="font-display text-2xl font-bold tracking-tight text-slate-950 leading-snug">{q.text}</div>
          <div className="mt-6 space-y-3">
            {q.options.map((opt, i) => {
              const active = answers[current] === i;
              return (
                <button
                  key={i}
                  onClick={() => pick(i)}
                  data-testid={`quiz-option-${i}`}
                  className={`w-full text-left border rounded-sm p-4 transition-colors flex items-center gap-4 ${
                    active
                      ? "border-orange-600 bg-orange-50"
                      : "border-slate-200 hover:border-orange-500"
                  }`}
                >
                  <div className={`w-6 h-6 grid place-items-center rounded-sm text-xs font-bold ${active ? "bg-orange-600 text-white" : "bg-slate-100 text-slate-700"}`}>
                    {String.fromCharCode(65 + i)}
                  </div>
                  <div className="flex-1 text-slate-800">{opt}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Nav */}
        <div className="mt-10 flex items-center justify-between gap-3">
          <button
            onClick={() => setCurrent((c) => Math.max(0, c - 1))}
            disabled={current === 0}
            data-testid="quiz-prev-btn"
            className="px-5 py-3 border border-slate-300 rounded-sm font-semibold text-slate-800 disabled:opacity-40 hover:bg-slate-50"
          >
            Previous
          </button>
          {current < total - 1 ? (
            <button
              onClick={() => setCurrent((c) => Math.min(total - 1, c + 1))}
              data-testid="quiz-next-btn"
              className="bg-slate-950 hover:bg-slate-800 text-white px-5 py-3 rounded-sm font-semibold inline-flex items-center gap-2"
            >
              Next <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={submit}
              disabled={!answeredAll || submitting}
              data-testid="quiz-submit-btn"
              className="bg-orange-600 hover:bg-orange-700 disabled:opacity-50 text-white px-6 py-3 rounded-sm font-semibold inline-flex items-center gap-2"
            >
              {submitting ? "Submitting…" : "Submit quiz"}
            </button>
          )}
        </div>

        {/* Question pager */}
        <div className="mt-10 flex flex-wrap gap-2">
          {quiz.questions.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrent(i)}
              data-testid={`quiz-pager-${i}`}
              className={`w-8 h-8 rounded-sm text-xs font-bold ${
                i === current
                  ? "bg-orange-600 text-white"
                  : answers[i] >= 0
                  ? "bg-slate-900 text-white"
                  : "bg-slate-100 text-slate-600"
              }`}
            >
              {i + 1}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function QuizResult({ quiz, result, onRetake }) {
  const navigate = useNavigate();
  const passed = result.attempt.passed;
  const score = result.attempt.score;
  return (
    <div className="min-h-screen bg-white" data-testid="quiz-result-page">
      <div className="max-w-3xl mx-auto p-6 lg:p-10">
        <div className={`border rounded-sm p-8 ${passed ? "border-green-600 bg-green-50" : "border-orange-600 bg-orange-50"}`}>
          <div className="flex items-center gap-3">
            {passed ? (
              <Trophy className="w-8 h-8 text-green-700" />
            ) : (
              <RotateCcw className="w-8 h-8 text-orange-700" />
            )}
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-700">
              {passed ? "Passed" : "Not quite there"}
            </div>
          </div>
          <h1 className="font-display text-5xl font-black tracking-tighter text-slate-950 mt-4">{score}%</h1>
          <p className="text-slate-700 mt-2">
            {passed
              ? "Excellent. This module counts toward your Platinum 1 Certified credential."
              : `You need at least ${quiz.pass_score}% to pass. Review the lessons and try again.`}
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            {passed ? (
              <Link to="/dashboard" data-testid="result-dashboard-btn" className="bg-slate-950 text-white px-5 py-3 rounded-sm font-semibold inline-flex items-center gap-2">
                Back to dashboard <ArrowRight className="w-4 h-4" />
              </Link>
            ) : (
              <button onClick={onRetake} data-testid="result-retake-btn" className="bg-orange-600 hover:bg-orange-700 text-white px-5 py-3 rounded-sm font-semibold inline-flex items-center gap-2">
                <RotateCcw className="w-4 h-4" /> Retake quiz
              </button>
            )}
            <button onClick={() => navigate(-1)} data-testid="result-back-course-btn" className="border border-slate-300 hover:bg-white px-5 py-3 rounded-sm font-semibold text-slate-900">
              Back to module
            </button>
          </div>
        </div>

        <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 mt-12">Review</h2>
        <div className="mt-4 space-y-4">
          {result.review.map((r, i) => (
            <div key={r.question_id} className="border border-slate-200 rounded-sm p-5">
              <div className="flex items-start gap-3">
                {r.correct ? <CheckCircle2 className="w-5 h-5 text-green-600 mt-1" /> : <XCircle className="w-5 h-5 text-red-600 mt-1" />}
                <div className="flex-1">
                  <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Question {i + 1}</div>
                  <div className="font-display font-bold text-slate-950 mt-1">{r.text}</div>
                  <div className="mt-3 space-y-1.5">
                    {r.options.map((opt, oi) => (
                      <div
                        key={oi}
                        className={`text-sm px-3 py-2 rounded-sm border ${
                          oi === r.correct_index
                            ? "border-green-600 bg-green-50 text-green-900"
                            : oi === r.selected_index
                            ? "border-red-500 bg-red-50 text-red-900"
                            : "border-slate-200 text-slate-700"
                        }`}
                      >
                        <span className="font-mono text-xs mr-2">{String.fromCharCode(65 + oi)}.</span>
                        {opt}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
